import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import ProgressDashes from '../../components/ProgressDashes';
import { StoryNavigation } from '../../components/StoryNavigation';
import { useStoryNavigation } from '../../hooks/useStoryNavigation';

interface CategoryStats {
  category: string;
  amount: number;
  percentage: number;
  confidence?: number;
  subcategories?: string[];
}

const SpendingCategoriesPage = () => {
  const navigate = useNavigate();
  const transactions = useSelector((state: RootState) => state.transactions.items);
  
  const categories = useMemo(() => {
    // Group transactions by category
    const categoryTotals = new Map<string, CategoryStats>();
    let totalSpending = 0;

    transactions.forEach(transaction => {
      if (transaction.amount < 0) { // Only count spending
        const amount = Math.abs(transaction.amount);
        const category = transaction.category || 'Uncategorized';
        
        if (!categoryTotals.has(category)) {
          categoryTotals.set(category, {
            category,
            amount: 0,
            percentage: 0,
            confidence: transaction.confidence,
            subcategories: transaction.subcategories
          });
        }
        
        const stats = categoryTotals.get(category)!;
        stats.amount += amount;
        totalSpending += amount;
      }
    });

    // Calculate percentages and convert to array
    return Array.from(categoryTotals.values())
      .map(stats => ({
        ...stats,
        percentage: (stats.amount / totalSpending) * 100
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5); // Get top 5
  }, [transactions]);

  const { goToNext, goToPrev, hasNext, hasPrev } = useStoryNavigation();

  // Auto-navigate to the next page after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/insights/expensive-day');
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-[#221A51]/[0.07] relative">
      <StoryNavigation
        onNext={goToNext}
        onPrev={goToPrev}
        hasNext={hasNext}
        hasPrev={hasPrev}
      />
      <div className="px-4 sm:px-16 pt-16 sm:pt-20 max-w-4xl mx-auto">
        <ProgressDashes currentStep={6} totalSteps={11} />

        <div className="px-2 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2"
          >
            <h1 className="text-[28px] sm:text-[34px] leading-[140%] font-black text-[#221A51] mb-[42px]">
              Your Top Spending Categories
            </h1>
            <div className="space-y-4 sm:space-y-6">
              {categories.map((category, index) => (
                <motion.div
                  key={`${category.category}-${index}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-4 sm:p-6 border-2 border-[#221A51] shadow-[4px_4px_0px_0px_#221A51]"
                >
                  <div className="flex flex-col space-y-1">
                    <h3 className="text-base sm:text-lg text-[#221A51]">{category.category}</h3>
                    <span className="text-xl sm:text-2xl font-bold text-[#221A51]">
                      {formatCurrency(category.amount)}
                    </span>
                  </div>
                  <div className="relative h-3 bg-[#E9E5FF] rounded-full overflow-hidden mt-3">
                    <div
                      className="absolute top-0 left-0 h-full bg-[#8364FF] rounded-full"
                      style={{ width: `${category.percentage}%` }}
                    />
                  </div>
                  <div className="mt-2 text-sm text-[#221A51]/60">
                    <span>{category.percentage.toFixed(1)}% of total</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SpendingCategoriesPage;
